import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const t = (text) => {
    if (typeof window !== 'undefined' && typeof window.__ === 'function') {
        try {
            return window.__(text);
        } catch (error) {
            return text;
        }
    }

    return text;
};

const emptyPlateForm = {
    listId: '',
    plate: '',
    contract: '',
    note: '',
    active: true,
    originalListId: '',
    originalPlate: '',
};

const emptyListForm = {
    name: '',
    description: '',
};

const normalizePlate = (value) => String(value || '').trim().toUpperCase();

const normalizeListPayload = (list = {}) => ({
    ...list,
    license_plate_entries: Array.isArray(list?.license_plate_entries)
        ? list.license_plate_entries
        : (Array.isArray(list?.license_plates) ? list.license_plates : []),
});

/**
 * Edit one person's plate-list assignments directly from the person page.
 */
export default function PersonPlateAssignments({ person = {}, lists = [], endpoints = {}, csrfToken = '' }) {
    const [currentLists, setCurrentLists] = useState(Array.isArray(lists) ? lists.map(normalizeListPayload) : []);
    const [feedback, setFeedback] = useState(null);
    const [saving, setSaving] = useState(false);
    const [plateDialogOpen, setPlateDialogOpen] = useState(false);
    const [listDialogOpen, setListDialogOpen] = useState(false);
    const [plateForm, setPlateForm] = useState(emptyPlateForm);
    const [listForm, setListForm] = useState(emptyListForm);
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('list');
    const [sortDirection, setSortDirection] = useState('asc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const apiFetch = async (url, options = {}) => {
        const response = await fetch(url, {
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'application/json',
                ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
                ...options.headers,
            },
            ...options,
        });

        if (!response.ok) {
            let payload = {};
            try {
                payload = await response.json();
            } catch (error) {
                payload = {};
            }

            throw new Error(payload.message || t('Request failed.'));
        }

        return response.json();
    };

    const plateRows = useMemo(() => {
        const rows = currentLists.flatMap((list) => (
            Array.isArray(list.license_plate_entries) ? list.license_plate_entries : []
        ).map((entry) => ({
            listId: Number(list.id),
            listName: String(list.name || ''),
            plate: normalizePlate(entry.plate),
            contract: String(entry.contract || ''),
            note: String(entry.note || ''),
            active: entry.active !== false,
        })));

        const query = search.trim().toLowerCase();
        const filtered = query === ''
            ? rows
            : rows.filter((row) => `${row.listName} ${row.plate} ${row.contract} ${row.note}`.toLowerCase().includes(query));

        const direction = sortDirection === 'desc' ? -1 : 1;
        return filtered.sort((left, right) => {
            const leftValue = String(left[sortField] || '');
            const rightValue = String(right[sortField] || '');
            return leftValue.localeCompare(rightValue) * direction;
        });
    }, [currentLists, search, sortDirection, sortField]);

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return plateRows.slice(start, start + rowsPerPage);
    }, [page, plateRows, rowsPerPage]);

    const replaceListEntries = async (listId, nextEntries) => {
        const url = (endpoints.replace || '').replace('__LIST_ID__', String(listId));
        const result = await apiFetch(url, {
            method: 'PUT',
            body: JSON.stringify({
                entries: nextEntries.map((entry) => ({
                    plate: normalizePlate(entry.plate),
                    contract: entry.contract || '',
                    note: entry.note || '',
                    active: entry.active !== false,
                })),
            }),
        });

        setCurrentLists((current) => current.map((list) => (
            Number(list.id) === Number(listId) ? normalizeListPayload(result.data) : list
        )));
    };

    const openCreateListDialog = () => {
        setListForm(emptyListForm);
        setListDialogOpen(true);
    };

    const createListAssignment = async () => {
        if (!listForm.name.trim()) {
            setFeedback({ severity: 'error', message: t('List name is required.') });
            return;
        }

        setSaving(true);
        try {
            const result = await apiFetch(endpoints.createList, {
                method: 'POST',
                body: JSON.stringify({
                    type: 'license_plate',
                    name: listForm.name.trim(),
                    description: listForm.description.trim(),
                    assigned_person_id: Number(person.id),
                    values: [],
                    source_lists: [],
                }),
            });

            setCurrentLists((current) => [...current, normalizeListPayload(result.data)].sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''))));
            setListDialogOpen(false);
            setFeedback({ severity: 'success', message: t('Plate list assignment created.') });
        } catch (error) {
            setFeedback({ severity: 'error', message: error.message || t('Failed to create plate list assignment.') });
        } finally {
            setSaving(false);
        }
    };

    const openAddPlateDialog = () => {
        if (currentLists.length === 0) {
            setFeedback({ severity: 'info', message: t('Create a plate list assignment first, then add numbers to it.') });
            return;
        }

        setPlateForm({
            ...emptyPlateForm,
            listId: currentLists[0] ? String(currentLists[0].id) : '',
        });
        setPlateDialogOpen(true);
    };

    const openEditPlateDialog = (row) => {
        setPlateForm({
            listId: String(row.listId),
            plate: row.plate,
            contract: row.contract,
            note: row.note,
            active: row.active,
            originalListId: String(row.listId),
            originalPlate: row.plate,
        });
        setPlateDialogOpen(true);
    };

    const savePlateAssignment = async () => {
        if (!plateForm.listId || !normalizePlate(plateForm.plate)) {
            setFeedback({ severity: 'error', message: t('Select a list and provide a valid plate.') });
            return;
        }

        setSaving(true);
        try {
            const targetList = currentLists.find((list) => Number(list.id) === Number(plateForm.listId));
            if (!targetList) {
                throw new Error(t('Selected list was not found.'));
            }

            let nextEntries = Array.isArray(targetList.license_plate_entries) ? [...targetList.license_plate_entries] : [];
            const nextEntry = {
                plate: normalizePlate(plateForm.plate),
                contract: plateForm.contract,
                note: plateForm.note,
                active: plateForm.active,
            };

            if (plateForm.originalListId && Number(plateForm.originalListId) !== Number(plateForm.listId)) {
                const originalList = currentLists.find((list) => Number(list.id) === Number(plateForm.originalListId));
                if (originalList) {
                    const filteredOriginalEntries = (originalList.license_plate_entries || []).filter((entry) => normalizePlate(entry.plate) !== plateForm.originalPlate);
                    await replaceListEntries(originalList.id, filteredOriginalEntries);
                }
            } else if (plateForm.originalPlate) {
                nextEntries = nextEntries.filter((entry) => normalizePlate(entry.plate) !== plateForm.originalPlate);
            }

            nextEntries.push(nextEntry);
            await replaceListEntries(targetList.id, nextEntries);
            setPlateDialogOpen(false);
            setFeedback({ severity: 'success', message: t('Plate saved.') });
        } catch (error) {
            setFeedback({ severity: 'error', message: error.message || t('Failed to save plate.') });
        } finally {
            setSaving(false);
        }
    };

    const removePlate = async (row) => {
        if (!window.confirm(t('Remove this plate from the assigned list?'))) {
            return;
        }

        setSaving(true);
        try {
            const targetList = currentLists.find((list) => Number(list.id) === Number(row.listId));
            if (!targetList) {
                throw new Error(t('Selected list was not found.'));
            }

            const nextEntries = (targetList.license_plate_entries || []).filter((entry) => normalizePlate(entry.plate) !== row.plate);
            await replaceListEntries(targetList.id, nextEntries);
            setFeedback({ severity: 'success', message: t('Plate removed.') });
        } catch (error) {
            setFeedback({ severity: 'error', message: error.message || t('Failed to remove plate.') });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack spacing={2}>
            {feedback && <Alert severity={feedback.severity}>{feedback.message}</Alert>}

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                <Box>
                    <Typography variant="subtitle1">{t('Linked plate assignments')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('Create a list assignment for this person first, then manage all plates inside that person list.')}
                    </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreateListDialog}>
                        {t('Create plate list assignment')}
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openAddPlateDialog} disabled={currentLists.length === 0}>
                        {t('Add plate')}
                    </Button>
                </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                    fullWidth
                    label={t('Filter by list or plate')}
                    value={search}
                    onChange={(event) => {
                        setSearch(event.target.value);
                        setPage(0);
                    }}
                />
                <TextField
                    select
                    label={t('Sort by')}
                    value={sortField}
                    onChange={(event) => setSortField(event.target.value)}
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value="listName">{t('List')}</MenuItem>
                    <MenuItem value="plate">{t('Plate')}</MenuItem>
                    <MenuItem value="contract">{t('Contract')}</MenuItem>
                </TextField>
                <TextField
                    select
                    label={t('Direction')}
                    value={sortDirection}
                    onChange={(event) => setSortDirection(event.target.value)}
                    sx={{ minWidth: 160 }}
                >
                    <MenuItem value="asc">{t('Ascending')}</MenuItem>
                    <MenuItem value="desc">{t('Descending')}</MenuItem>
                </TextField>
            </Stack>

            <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('List')}</TableCell>
                            <TableCell>{t('Plate')}</TableCell>
                            <TableCell>{t('Contract')}</TableCell>
                            <TableCell>{t('Status')}</TableCell>
                            <TableCell>{t('Note')}</TableCell>
                            <TableCell align="right">{t('Actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {currentLists.length === 0 ? t('No plate list assignments exist for this person yet.') : t('No matching plates for the current filters.')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {paginatedRows.map((row) => (
                            <TableRow key={`${row.listId}-${row.plate}`}>
                                <TableCell>{row.listName}</TableCell>
                                <TableCell>{row.plate}</TableCell>
                                <TableCell>{row.contract || ' - '}</TableCell>
                                <TableCell>{row.active ? t('Active') : t('Inactive')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{row.note || ' - '}</TableCell>
                                <TableCell align="right">
                                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEditPlateDialog(row)}>
                                        {t('Edit')}
                                    </Button>
                                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => removePlate(row)}>
                                        {t('Delete')}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>

            <TablePagination
                component="div"
                count={plateRows.length}
                page={page}
                onPageChange={(event, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
            />

            <Dialog open={listDialogOpen} onClose={() => setListDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{t('Create plate list assignment')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label={t('List name')}
                            value={listForm.name}
                            onChange={(event) => setListForm((current) => ({ ...current, name: event.target.value }))}
                        />
                        <TextField
                            label={t('Description')}
                            multiline
                            minRows={2}
                            value={listForm.description}
                            onChange={(event) => setListForm((current) => ({ ...current, description: event.target.value }))}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setListDialogOpen(false)}>{t('Cancel')}</Button>
                    <Button variant="contained" onClick={createListAssignment} disabled={saving}>{t('Create')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={plateDialogOpen} onClose={() => setPlateDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{plateForm.originalPlate ? t('Edit plate') : t('Add plate')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label={t('List')}
                            value={plateForm.listId}
                            onChange={(event) => setPlateForm((current) => ({ ...current, listId: event.target.value }))}
                        >
                            {currentLists.map((list) => (
                                <MenuItem key={list.id} value={String(list.id)}>{list.name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label={t('Plate')}
                            value={plateForm.plate}
                            onChange={(event) => setPlateForm((current) => ({ ...current, plate: normalizePlate(event.target.value) }))}
                        />
                        <TextField
                            label={t('Contract')}
                            value={plateForm.contract}
                            onChange={(event) => setPlateForm((current) => ({ ...current, contract: event.target.value }))}
                        />
                        <FormControlLabel
                            control={<Switch checked={plateForm.active} onChange={(event) => setPlateForm((current) => ({ ...current, active: event.target.checked }))} />}
                            label={t('Active')}
                        />
                        <TextField
                            label={t('Note')}
                            multiline
                            minRows={3}
                            value={plateForm.note}
                            onChange={(event) => setPlateForm((current) => ({ ...current, note: event.target.value }))}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPlateDialogOpen(false)}>{t('Cancel')}</Button>
                    <Button variant="contained" onClick={savePlateAssignment} disabled={saving}>{t('Save')}</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
